import { Key, memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import cx from 'classnames'
import Styles from './SegmentedControl.module.scss'
import { SegmentedControlItemProps, SegmentedControlProps } from './Types'
import { useButton } from '@react-aria/button'
import { useHover } from '@react-aria/interactions'
import { useCollection } from '@react-stately/collections'
import { Collection, Node } from '@react-types/shared'
import { mergeProps } from '@react-aria/utils'

const Item = memo<SegmentedControlItemProps>(
  ({ registerRef, onClick, active, isDisabled, variant, size = 'md', children, ...labellingProps }) => {
    const elRef = useRef<HTMLButtonElement | null>(null)
    const { hoverProps } = useHover({})

    const { buttonProps } = useButton(
      {
        isDisabled: isDisabled,
        onPress: () => onClick(),
      },
      elRef,
    )

    return (
      <button
        {...mergeProps(hoverProps, buttonProps, labellingProps)}
        className={cx(Styles.item, Styles[variant], Styles[size], {
          [Styles.itemActive]: active,
          [Styles.itemDisabled]: isDisabled,
        })}
        aria-pressed={active}
        ref={(node) => {
          elRef.current = node
          registerRef(node)
        }}
        disabled={isDisabled}
      >
        {children}
      </button>
    )
  },
)

function elementWidth(element: HTMLElement) {
  return element.getBoundingClientRect().width
}

type ItemsRefMap = { [key: string]: HTMLButtonElement | null }

const SegmentedControl = ({
  activeItem,
  onItemClick,
  isDisabled = false,
  isFullWidth,
  variant = 'primary',
  rounded = false,
  className,
  children,
  items,
  size = 'md',
}: SegmentedControlProps) => {
  const [showHighlighter, setShowHighlighter] = useState(false)
  const { hoverProps } = useHover({})
  const itemsRefs = useRef<ItemsRefMap>({})
  const registerRef = useCallback((name: Key) => (ref: any) => (itemsRefs.current[name] = ref), [])
  const currentActiveItemEl = itemsRefs.current[activeItem]
  const [activeElemWidth, setActiveElemWidth] = useState(0)
  const collection = [...useCollection({ children, items }, (item) => item as Collection<Node<any>>)]

  useEffect(() => {
    if (!currentActiveItemEl) return

    setActiveElemWidth(elementWidth(currentActiveItemEl))

    const currentItemResizeObserver = new ResizeObserver(() => setActiveElemWidth(elementWidth(currentActiveItemEl)))
    currentItemResizeObserver.observe(currentActiveItemEl)

    return () => currentItemResizeObserver.disconnect()
  }, [currentActiveItemEl])

  useLayoutEffect(() => {
    setShowHighlighter(true)
  }, [currentActiveItemEl])

  return (
    <div
      className={cx(Styles.root, Styles[variant], className, {
        [Styles.isDisabled]: isDisabled,
        [Styles.isFullWidth]: isFullWidth,
        [Styles.rounded]: rounded,
      })}
      {...hoverProps}
    >
      {currentActiveItemEl && showHighlighter && (
        <div
          className={cx(Styles.highlighter, Styles[variant], Styles[size])}
          style={{
            transform: `translateX(${currentActiveItemEl.offsetLeft}px)`,
            width: `${activeElemWidth}px`,
          }}
        />
      )}
      {collection.map((item) => (
        <Item
          key={item.key}
          active={activeItem === item.key}
          registerRef={registerRef(item.key)}
          onClick={() => onItemClick(item.key)}
          isDisabled={isDisabled}
          variant={variant}
          aria-label={item.textValue}
          size={size}
        >
          {item.rendered}
        </Item>
      ))}
    </div>
  )
}

export { SegmentedControl }
